import { Container } from 'pixi.js';

/**
 * Camera system that follows a target (the player).
 * Offsets a world container so the player stays centered on screen.
 * Clamps to world bounds so we don't show empty space.
 */
export class CameraSystem {
  private worldContainer: Container;
  private screenW: number;
  private screenH: number;
  worldW: number;
  worldH: number;

  // Smooth follow
  private lerp = 0.1;

  constructor(worldContainer: Container, screenW: number, screenH: number, worldW: number, worldH: number) {
    this.worldContainer = worldContainer;
    this.screenW = screenW;
    this.screenH = screenH;
    this.worldW = worldW;
    this.worldH = worldH;
  }

  /** Update screen size (on resize) */
  setScreenSize(w: number, h: number) {
    this.screenW = w;
    this.screenH = h;
  }

  /** Update world size (if map expands) */
  setWorldSize(w: number, h: number) {
    this.worldW = w;
    this.worldH = h;
  }

  /**
   * Follow a target position (in world coordinates).
   * Call each frame. Uses lerp for smooth follow.
   * topOffset: pixels from top of screen that should NOT be covered (HUD).
   */
  follow(targetX: number, targetY: number, topOffset = 50) {
    // Desired camera center: player should be centered in the playable area
    const playableH = this.screenH - topOffset;
    const desiredX = -(targetX - this.screenW / 2);
    const desiredY = -(targetY - topOffset - playableH / 2);

    let clampedX: number;
    let clampedY: number;

    // When screen is wider/taller than world, center the world
    if (this.screenW >= this.worldW) {
      clampedX = (this.screenW - this.worldW) / 2;
    } else {
      const minX = -(this.worldW - this.screenW);
      clampedX = Math.max(minX, Math.min(0, desiredX));
    }

    if (this.screenH >= this.worldH) {
      clampedY = (this.screenH - this.worldH) / 2;
    } else {
      const minY = -(this.worldH - this.screenH);
      clampedY = Math.max(minY, Math.min(0, desiredY));
    }

    // Smooth lerp
    this.worldContainer.x += (clampedX - this.worldContainer.x) * this.lerp;
    this.worldContainer.y += (clampedY - this.worldContainer.y) * this.lerp;
  }

  /** Snap camera immediately (no lerp) */
  snapTo(targetX: number, targetY: number, topOffset = 50) {
    const playableH = this.screenH - topOffset;
    const desiredX = -(targetX - this.screenW / 2);
    const desiredY = -(targetY - topOffset - playableH / 2);

    if (this.screenW >= this.worldW) {
      this.worldContainer.x = (this.screenW - this.worldW) / 2;
    } else {
      const minX = -(this.worldW - this.screenW);
      this.worldContainer.x = Math.max(minX, Math.min(0, desiredX));
    }

    if (this.screenH >= this.worldH) {
      this.worldContainer.y = (this.screenH - this.worldH) / 2;
    } else {
      const minY = -(this.worldH - this.screenH);
      this.worldContainer.y = Math.max(minY, Math.min(0, desiredY));
    }
  }

  /** Convert screen coordinates to world coordinates */
  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: screenX - this.worldContainer.x,
      y: screenY - this.worldContainer.y,
    };
  }

  /** Convert world coordinates to screen coordinates */
  worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    return {
      x: worldX + this.worldContainer.x,
      y: worldY + this.worldContainer.y,
    };
  }
}
